import React from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { SEO_KEYWORDS } from "@/seo/seoData";
import { motion } from "framer-motion";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  ArrowRight,
  Users,
  Target,
  Layers,
  Globe,
  Wrench,
  Code,
  CheckCircle,
} from "lucide-react";

const beliefs = [
  {
    icon: Target,
    title: "Strategy before execution",
    description:
      "We define what the business actually needs before recommending services or building anything.",
  },
  {
    icon: Layers,
    title: "Custom solutions, not templates",
    description:
      "Every business has different goals, users, and challenges. The solution should reflect that.",
  },
  {
    icon: Globe,
    title: "Built for real use",
    description:
      "Whether it is a website, SEO setup, content direction, or custom tool, the work should support real business use.",
  },
  {
    icon: Wrench,
    title: "Support beyond launch",
    description:
      "We stay involved after launch with improvements, fixes, and ongoing support where needed.",
  },
];

const services = [
  {
    icon: Globe,
    title: "Website Development",
    description: "Custom websites built for speed, clarity, and conversion.",
    link: "/services/website-development",
  },
  {
    icon: Target,
    title: "SEO Services",
    description:
      "Search engine optimization services that help the right clients find you.",
    link: "/services/seo-services",
  },
  {
    icon: Users,
    title: "Social Media Marketing",
    description:
      "Content and strategy that help your business stay visible and build trust.",
    link: "/services/social-media-marketing",
  },
  {
    icon: Code,
    title: "Custom Tool Creation",
    description:
      "Dashboards, calculators, and custom digital tools built around specific business needs.",
    link: "/services/custom-tool-creation",
  },
  {
    icon: Wrench,
    title: "Maintenance & Support",
    description:
      "Ongoing updates, fixes, and technical support that keep your website performing well.",
    link: "/services/maintenance-support",
  },
];

export default function AboutPage() {
  return (
    <Layout>
      <SEO
        title="Know About us | Square Trigger"
        description="Know more about Square Trigger to get exceptional Digital Agency services in Vancouver and throughout Canada. Read more."
        keywords={SEO_KEYWORDS.about}
      />

      {/* Hero Section */}
      <section className="pt-24 sm:pt-28 lg:pt-32 pb-16 sm:pb-18 lg:pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-foreground via-foreground/95 to-foreground/90" />
        <div className="absolute inset-0">
          <div className="absolute top-16 sm:top-20 right-6 sm:right-20 w-72 sm:w-96 h-72 sm:h-96 bg-primary/20 rounded-full blur-3xl" />
          <div className="absolute bottom-16 sm:bottom-20 left-6 sm:left-20 w-56 sm:w-72 h-56 sm:h-72 bg-secondary/20 rounded-full blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Badge className="mb-6 bg-primary/20 text-primary-foreground border-none">
                <Users className="h-4 w-4 mr-2" />
                About Us
              </Badge>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-background mb-5 sm:mb-6 leading-tight"
            >
              A Digital Agency in Vancouver Built Around Real Business Needs
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-lg sm:text-xl text-background/70 mb-8 max-w-3xl mx-auto leading-relaxed"
            >
              Square Trigger helps businesses grow through website development,
              SEO services, social media marketing, custom tool creation, and
              ongoing support — built around what the business actually needs,
              not a generic package.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-center justify-center"
            >
              <Button
                variant="gradient"
                size="lg"
                asChild
                className="group w-full sm:w-auto justify-center"
              >
                <Link
                  to="/contact"
                  className="flex items-center justify-center gap-2"
                >
                  Book a Free Consultation
                  <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>

              <Button
                variant="glass"
                size="lg"
                asChild
                className="w-full sm:w-auto justify-center text-white bg-white/10 border border-white/20 hover:shadow-[0_8px_30px_rgba(255,255,255,0.12)] hover:-translate-y-[1px] transition-all duration-300"
              >
                <Link to="/services/website-development">View Our Services</Link>
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Who We Are */}
      <section className="pt-12 sm:pt-14 lg:pt-20 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-24 left-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl" />
          <div className="absolute -bottom-24 right-10 w-72 h-72 bg-secondary/5 rounded-full blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-5xl mx-auto text-center"
          >
            <span className="text-sm font-medium text-primary uppercase tracking-wider">
              Who We Are
            </span>

            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mt-3 mb-5">
              We Start With the Problem. Then We Build the Right Solution.
            </h2>

            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-3xl mx-auto mb-6">
              Square Trigger is a digital agency in Vancouver that works with
              businesses that need{" "}
              <Link
                to="/services/website-development"
                className="text-primary hover:underline"
              >
                stronger websites
              </Link>
              ,{" "}
              <Link
                to="/services/seo-services"
                className="text-primary hover:underline"
              >
                better visibility
              </Link>
              ,{" "}
              <Link
                to="/services/social-media-marketing"
                className="text-primary hover:underline"
              >
                clearer messaging
              </Link>
              , and smarter{" "}
              <Link
                to="/services/custom-tool-creation"
                className="text-primary hover:underline"
              >
                digital systems
              </Link>
              .
            </p>

            <div className="bg-card/60 backdrop-blur rounded-2xl border border-border shadow-soft p-6 sm:p-8">
              <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-3xl mx-auto">
                We do not lead with one-size-fits-all packages. We start by
                understanding what is slowing the business down online, then
                build the right solution around that need.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What We Believe */}
      <section className="pt-12 sm:pt-14 lg:pt-14 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-10"
          >
            <span className="text-sm font-medium text-primary uppercase tracking-wider">
              What We Believe
            </span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mt-3">
              Good Digital Work Starts With Clarity
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {beliefs.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.08 }}
                  className="p-6 bg-card rounded-2xl border border-border shadow-soft hover:shadow-medium transition-shadow text-center sm:text-left flex flex-col items-center sm:items-start"
                >
                  <div className="w-10 h-10 rounded-xl gradient-primary-soft flex items-center justify-center mb-4">
                    <Icon className="h-5 w-5 text-primary" />
                  </div>

                  <h3 className="font-semibold text-foreground mb-2">
                    {item.title}
                  </h3>

                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {item.title === "Built for real use" ? (
                      <>
                        Whether it is a{" "}
                        <Link
                          to="/services/website-development"
                          className="text-primary hover:underline"
                        >
                          website
                        </Link>
                        ,{" "}
                        <Link
                          to="/services/seo-services"
                          className="text-primary hover:underline"
                        >
                          SEO setup
                        </Link>
                        ,{" "}
                        <Link
                          to="/services/social-media-marketing"
                          className="text-primary hover:underline"
                        >
                          content direction
                        </Link>
                        , or{" "}
                        <Link
                          to="/services/custom-tool-creation"
                          className="text-primary hover:underline"
                        >
                          custom tool
                        </Link>
                        , the work should support real business use.
                      </>
                    ) : item.title === "Support beyond launch" ? (
                      <>
                        We stay involved after launch with improvements, fixes,
                        and{" "}
                        <Link
                          to="/services/maintenance-support"
                          className="text-primary hover:underline"
                        >
                          ongoing support
                        </Link>{" "}
                        where needed.
                      </>
                    ) : (
                      item.description
                    )}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="section-padding bg-foreground text-background relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 left-10 w-80 h-80 bg-primary/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-80 h-80 bg-secondary/10 rounded-full blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-12 sm:mb-16"
          >
            <span className="text-sm font-medium text-secondary uppercase tracking-wider">
              What We Do
            </span>
            <h2 className="text-3xl md:text-4xl font-display font-bold mt-3">
              The Services Behind the <span className="gradient-text">Strategy</span>
            </h2>
            <p className="text-background/70 leading-relaxed mt-4 max-w-3xl mx-auto">
              We help businesses strengthen their digital presence through
              focused services that work better together, not in isolation.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                  className="h-full"
                >
                  <Card className="p-6 h-full bg-background/5 border border-background/10 hover:border-primary/30 transition-colors text-center sm:text-left flex flex-col items-center sm:items-start">
                    <div className="w-12 h-12 rounded-xl gradient-primary flex items-center justify-center mb-4">
                      <Icon className="h-6 w-6 text-primary-foreground" />
                    </div>

                    <h3 className="text-xl font-display font-bold mb-3 text-background">
                      {service.title}
                    </h3>

                    <p className="text-background/70 leading-relaxed mb-5">
                      {service.description}
                    </p>

                    <Link
                      to={service.link}
                      className="inline-flex items-center gap-2 text-primary hover:underline mt-auto"
                    >
                      Learn More
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="pt-12 sm:pt-14 lg:pt-16 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-4 sm:mb-6">
              Ready to Work With a Digital Agency That Builds Around Your
              Business?
            </h2>

            <p className="text-base sm:text-lg text-muted-foreground mb-8 leading-relaxed max-w-2xl mx-auto">
              Let&apos;s talk about what your business needs online and how we
              can help build the right solution.
            </p>

            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
              <Button
                variant="gradient"
                size="lg"
                asChild
                className="group w-full sm:w-auto justify-center"
              >
                <Link
                  to="/contact"
                  className="flex items-center justify-center gap-2"
                >
                  Book a Free Consultation
                  <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>

              <Button
                variant="outline"
                size="lg"
                asChild
                className="w-full sm:w-auto justify-center"
              >
                <Link
                  to="/contact"
                  className="flex items-center justify-center"
                >
                  Contact Us
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}